import React from "react";
import { AlertTriangle } from "lucide-react";

/**
 * Catches render errors anywhere under it so the user never sees a blank page.
 * Particularly useful in production where minified errors are hard to diagnose.
 */
export default class ErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep this in production to aid debugging from browser devtools.
    // eslint-disable-next-line no-console
    console.error("Render error caught by ErrorBoundary:", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6" data-testid="error-boundary">
          <div className="max-w-lg w-full bg-white rounded-2xl border border-rose-200 p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-rose-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-stone-900">Un problème est survenu</h2>
                <p className="text-sm text-stone-700 mt-1">
                  L'application a rencontré une erreur inattendue. Essayez de rafraîchir la page.
                  Si le problème persiste, contactez le support avec le message ci-dessous.
                </p>
                <pre className="mt-3 text-[11px] bg-stone-50 rounded-lg p-2 text-stone-700 whitespace-pre-wrap break-words border border-stone-200">
                  {String(this.state.error?.message || this.state.error)}
                </pre>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-full text-sm font-semibold"
                  >
                    Rafraîchir
                  </button>
                  <button
                    onClick={() => {
                      localStorage.removeItem("auth_token");
                      localStorage.removeItem("auth_user");
                      window.location.replace("/");
                    }}
                    className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-full text-sm font-semibold"
                  >
                    Se déconnecter
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
